"use client";

import { useState } from "react";
import {
  CopyButton,
  Notice,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type CallNumberScheme,
  type CallPart,
  type ParsedCallNumber,
  shelfOrder,
} from "@/lib/tool-kit/identifiers/call-number";
import { inputLines } from "@/lib/tool-kit/identifiers/identifier-input";

/** English strings. A translated content file overrides these through `ui`. */
export const CALL_NUMBER_SORTER_STRINGS = {
  inputLabel: "Call numbers — one per line",
  inputPlaceholder:
    "QA76.73.J38 S53 2019\nQA76.9.D3 E57 2016\nQA 76.73 .J38 2018\nQA76 .A1\nQA76.73.J38 S53 2015",
  inputHint:
    "Library of Congress (QA76.73.J38 S53 2019) or Dewey (005.133 SCH).",
  schemeLabel: "Classification",
  schemeAuto: "Detect automatically",
  schemeLc: "Library of Congress",
  schemeDewey: "Dewey Decimal",
  tryExample: "Try an example",
  exampleList:
    "QA76.9.D3 E57 2016\nQA 76.73 .J38 2018\nQA76.73.J38 S53 2019\nQA76 .A1\nPS3545.I345 Z6 1990\nQA76.73.J38 S53 2015\nPS3545.I345 1990\nQA76.73.J38",
  emptyPrompt: "Paste call numbers, one per line, to put them in shelf order.",
  results: "Shelf order",
  detected: "Read as {scheme} call numbers.",
  sortedCount:
    "one:{count} call number in order|other:{count} call numbers in order",
  copy: "Copy sorted list",
  copied: "Copied!",
  unreadableHeading: "Not sorted",
  unreadable:
    "one:{count} line is not a {scheme} call number. It is listed here and at the end of the copied list.|other:{count} lines are not {scheme} call numbers. They are listed here and at the end of the copied list.",
  partPrefix: "Location",
  partClass: "Class",
  partNumber: "Class number",
  partCutter: "Cutter",
  partBookMark: "Book mark",
  partDate: "Date",
  partVolume: "Volume",
  partCopy: "Copy",
  partPart: "Part",
  partDesignation: "Number",
  partOther: "Other",
} as const;

type Strings = typeof CALL_NUMBER_SORTER_STRINGS;
type SchemeChoice = CallNumberScheme | "auto";

interface Chip {
  label: string;
  value: string;
}

function partChip(
  part: CallPart,
  scheme: CallNumberScheme,
  strings: Strings
): Chip {
  switch (part.kind) {
    case "cutter":
      return {
        label: scheme === "lc" ? strings.partCutter : strings.partBookMark,
        value: part.text,
      };
    case "date":
      return { label: strings.partDate, value: part.text };
    case "designation": {
      const labels: Record<string, string> = {
        v: strings.partVolume,
        c: strings.partCopy,
        pt: strings.partPart,
      };
      return {
        label: labels[part.label] ?? strings.partDesignation,
        value: part.text,
      };
    }
    default:
      return { label: strings.partOther, value: part.text };
  }
}

function chipsFor(call: ParsedCallNumber, strings: Strings): Chip[] {
  const chips: Chip[] = [];
  if (call.prefix) {
    chips.push({ label: strings.partPrefix, value: call.prefix });
  }
  if (call.classLetters) {
    chips.push({ label: strings.partClass, value: call.classLetters });
  }
  if (call.classWhole) {
    const number = call.classDecimal
      ? `${call.classWhole}.${call.classDecimal}`
      : call.classWhole;
    chips.push({ label: strings.partNumber, value: number });
  }
  for (const part of call.parts) {
    chips.push(partChip(part, call.scheme, strings));
  }
  return chips;
}

export default function CallNumberSorterWidget() {
  const strings = useToolUi(CALL_NUMBER_SORTER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [choice, setChoice] = useState<SchemeChoice>("auto");

  const lines = inputLines(text);
  const order = lines.length > 0 ? shelfOrder(lines, choice) : null;
  const schemeName =
    order?.scheme === "dewey" ? strings.schemeDewey : strings.schemeLc;
  const copyText = order
    ? [
        ...order.sorted.map((call) => call.raw.trim()),
        ...order.unreadable,
      ].join("\n")
    : "";

  return (
    <section className="space-y-6">
      <TextAreaField
        hint={strings.inputHint}
        id="cns-input"
        label={strings.inputLabel}
        onChange={setText}
        placeholder={strings.inputPlaceholder}
        rows={8}
        value={text}
      />
      <div className="flex flex-wrap items-end gap-4">
        <div className="min-w-56">
          <SelectField
            id="cns-scheme"
            label={strings.schemeLabel}
            onChange={setChoice}
            options={[
              { value: "auto", label: strings.schemeAuto },
              { value: "lc", label: strings.schemeLc },
              { value: "dewey", label: strings.schemeDewey },
            ]}
            value={choice}
          />
        </div>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.exampleList)}
          type="button"
        >
          {strings.tryExample}
        </button>
      </div>

      <div aria-live="polite" className="space-y-4">
        {order === null ? (
          <Notice>{strings.emptyPrompt}</Notice>
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className={WIDGET_H2}>{strings.results}</h2>
                <p className="text-muted-foreground text-sm">
                  {t(strings.detected, { scheme: schemeName })}{" "}
                  {tPlural(strings.sortedCount, locale, order.sorted.length, {
                    count: fmt(order.sorted.length),
                  })}
                </p>
              </div>
              <CopyButton
                copiedLabel={strings.copied}
                label={strings.copy}
                text={copyText}
              />
            </div>
            <ol className="space-y-2">
              {order.sorted.map((call, index) => (
                <li
                  className={`${CARD} flex flex-wrap items-baseline gap-x-4 gap-y-2`}
                  key={`${index}-${call.raw}`}
                >
                  <span className="w-8 text-muted-foreground text-sm tabular-nums">
                    {fmt(index + 1)}
                  </span>
                  <span className="font-mono text-foreground">
                    {call.raw.trim()}
                  </span>
                  <span className="flex flex-wrap gap-1.5">
                    {chipsFor(call, strings).map((chip, chipIndex) => (
                      <span
                        className="rounded border border-border px-1.5 py-0.5 text-xs"
                        // biome-ignore lint/suspicious/noArrayIndexKey: parts are positional
                        key={chipIndex}
                      >
                        <span className="text-muted-foreground">
                          {chip.label}
                        </span>{" "}
                        <span className="font-mono text-foreground">
                          {chip.value}
                        </span>
                      </span>
                    ))}
                  </span>
                </li>
              ))}
            </ol>
            {order.unreadable.length > 0 ? (
              <div className="space-y-2">
                <h3 className={WIDGET_H3}>{strings.unreadableHeading}</h3>
                <Notice>
                  {tPlural(
                    strings.unreadable,
                    locale,
                    order.unreadable.length,
                    {
                      count: fmt(order.unreadable.length),
                      scheme: schemeName,
                    }
                  )}
                </Notice>
                <ul className="space-y-1 font-mono text-sm">
                  {order.unreadable.map((line, index) => (
                    <li key={`${index}-${line}`}>{line}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        )}
      </div>
    </section>
  );
}
